import {z} from "zod/v4";

import {LevelObjectSchema} from "./config/types.js";
import {
  CustomTimestamp,
  DocumentSchema,
  PointEntitySchema,
  ShardCollectedSchema,
} from "./types.js";

/* -------------------------------------------------------------------------- */
/*                          V2 REPLAY ENTITIES                                */
/* -------------------------------------------------------------------------- */

export const PlayerFinalStateSchema = z.strictObject({
  position: PointEntitySchema,
  velocity: PointEntitySchema,
  died: z.boolean(),
  collectedShards: z.array(ShardCollectedSchema),
});
export type PlayerFinalState = z.infer<typeof PlayerFinalStateSchema>;

/**
 * Contains the complete input history and final state for a single player's
 * participation in a round.
 */
export const PlayerReplaySchema = z.strictObject({
  id: z.string(),
  ownerId: z.string(),
  instanceId: z.number().int().nonnegative(),
  /**
   * Run-length encoded actions.
   * An array of integers representing alternating
   * runs of `false` and `true` values, always starting with `false`.
   * e.g., `[3, 2]` = 3 `false`s then 2 `true`s.
   * e.g., `[0, 5]` = 0 `false`s then 5 `true`s.
   */
  actions: z.array(z.number().int().nonnegative()),
  finalState: PlayerFinalStateSchema,
});
export type PlayerReplay = z.infer<typeof PlayerReplaySchema>;

/**
 * The new, unified ReplayEntity schema, optimized for size and lookup speed.
 */
export const ReplayEntitySchemaV2 = z.strictObject({
  schemaVersion: z.literal(2),
  levelId: z.string(),

  /**
   * A map of player UIDs to their individual replay data.
   * Using a record/map for efficient O(1) lookup of a player's data.
   */
  playerReplays: z.record(z.string().min(1), PlayerReplaySchema),

  // --- Top-Level Round Outcome ---
  completed: z.boolean(),
  /**
   * The total number of simulation steps the round lasted. This is the step
   * of the final action that led to the game's conclusion.
   */
  totalSteps: z.number(),
});
export type ReplayEntityV2 = z.infer<typeof ReplayEntitySchemaV2>;

/* -------------------------------------------------------------------------- */
/*                          V2 WIP LEVEL SCHEMAS                              */
/* -------------------------------------------------------------------------- */

export const WipLevelEntitySchemaV2 = z.strictObject({
  name: z.string(),
  creatorId: z.string(),
  data: z.array(LevelObjectSchema),
  createdAt: CustomTimestamp,
  updatedAt: CustomTimestamp,
});
export const WipLevelDocSchemaV2 = z.object({
  ...WipLevelEntitySchemaV2.shape,
  ...DocumentSchema.shape,
});

export type WipLevelEntityV2 = z.infer<typeof WipLevelEntitySchemaV2>;
export type WipLevelDocV2 = z.infer<typeof WipLevelDocSchemaV2>;

/* -------------------------------------------------------------------------- */
/*                          PUBLISHED LEVEL V2 SCHEMAS                        */
/* -------------------------------------------------------------------------- */

export const LevelEntitySchemaV2 = z.strictObject({
  name: z.string(),
  creatorId: z.string(),
  data: z.array(LevelObjectSchema),
  publishedAt: CustomTimestamp,
  updatedAt: CustomTimestamp,
  lastWorldRecordAt: CustomTimestamp.optional(),
  lastStatsUpdateAt: CustomTimestamp.optional(),
  worldRecordSteps: z.number().optional(),
  validated: z.boolean(),
  totalViews: z.number(), // The total count of players who viewed the level
  totalCompletions: z.number(), // The count of players who finished the level
  successRate: z.number(), // Computed as totalCompletions / totalViews
});
export const LevelDocSchemaV2 = z.object({
  ...LevelEntitySchemaV2.shape,
  ...DocumentSchema.shape,
});

export type LevelEntityV2 = z.infer<typeof LevelEntitySchemaV2>;
export type LevelDocV2 = z.infer<typeof LevelDocSchemaV2>;

/* -------------------------------------------------------------------------- */
/*                          PLAYLIST RELATED SCHEMAS                          */
/* -------------------------------------------------------------------------- */

export const PlaylistEntitySchema = z.strictObject({
  name: z.string(),
  creatorId: z.string(),
  description: z.string().optional(),
  levelIds: z.array(z.string()).default([]),
  featured: z.boolean().optional(),
  featuredRank: z.number().optional(),
  isImmutable: z.boolean().optional(), // Flag to indicate if the playlist can be edited by the user
  createdAt: CustomTimestamp,
  updatedAt: CustomTimestamp,
});

export const PlaylistDocSchema = z.object({
  ...PlaylistEntitySchema.shape,
  ...DocumentSchema.shape,
});

export type PlaylistEntity = z.infer<typeof PlaylistEntitySchema>;
export type PlaylistDoc = z.infer<typeof PlaylistDocSchema>;

export const CreatePlaylistSchema = z.strictObject({
  name: z.string(),
  levelIds: z.array(z.string()),
});

export const UpdatePlaylistSchema = z.object({
  name: z.string().nullish(),
  description: z.string().nullish(),
  levelIds: z.array(z.string()).nullish(),
  featured: z.boolean().nullish(),
  featuredRank: z.number().nullish(),
  ...DocumentSchema.shape,
});

export const AddLevelToPlaylistSchema = z.strictObject({
  levelId: z.string(),
  ...DocumentSchema.shape,
});

export const DeletePlaylistSchema = z.strictObject({
  id: z.string(),
});

/* -------------------------------------------------------------------------- */
/*                       PERSONAL BEST RELATED SCHEMAS                        */
/* -------------------------------------------------------------------------- */

// Define the schema for the V2-personal-best subcollection
export const V2PersonalBestStatsSchema = z.strictObject({
  totalReplays: z.number(),
  completions: z.number(),
  attemptsUntilFirstCompletion: z.number().nullable(),
  averageAttemptsForCompletions: z.number(),
  totalTimeSpent: z.number(), // in steps as a float
});

export const V2PersonalBestSchema = z.strictObject({
  userId: z.string(),
  levelId: z.string(),
  replay: ReplayEntitySchemaV2.nullable(),
  stats: V2PersonalBestStatsSchema,
  createdAt: CustomTimestamp,
  updatedAt: CustomTimestamp,
});

export type V2PersonalBestStats = z.infer<typeof V2PersonalBestStatsSchema>;
export type V2PersonalBest = z.infer<typeof V2PersonalBestSchema>;

/* -------------------------------------------------------------------------- */
/*                       REPLAY SUBMISSION SCHEMAS                            */
/* -------------------------------------------------------------------------- */

// Define the schema for V2 replay submissions
export const V2ReplaySubmissionSchema = z.strictObject({
  levelId: z.string(),
  userId: z.string(),
  replay: ReplayEntitySchemaV2,
});

// Define the schema for V2 replay submissions
export const V2ReplaySubmissionResponseSchema = z.strictObject({
  success: z.boolean(),
  isNewPersonalBest: z.boolean(),
  isNewWorldRecord: z.boolean(),
  replay: ReplayEntitySchemaV2,

  message: z.string().optional(),
});

export type V2ReplaySubmission = z.infer<typeof V2ReplaySubmissionSchema>;
export type V2ReplaySubmissionResponse = z.infer<
  typeof V2ReplaySubmissionResponseSchema
>;
